import React, { useEffect, useRef } from 'react';
import type { FilterBlock } from '../../../../services/admin/cloudAgentTypes';
import type { ChatTurn } from '../../../../stores/appCloudAgentStore';
import { ChatMessage } from './ChatMessage';

/** How far the user may scroll up before auto-scroll stops chasing the stream (px). */
const AUTO_SCROLL_PAUSE_PX = 120;

interface ChatTranscriptProps {
  turns: ChatTurn[];
  onFilterChange: (turnId: string, block: FilterBlock, selected: string[]) => void;
  /** Id of the turn currently being re-hydrated after a filter change, if any. */
  filteringTurnId: string | null;
  /** Why the last filter re-hydration failed, and on which turn to show it. */
  filterError: { turnId: string; message: string } | null;
}

/**
 * The scrolling list of turns. The page itself is the scroll container (AdminLayout has no inner
 * scroller), so auto-scroll works off the window: it follows the newest turn while the user sits
 * at the bottom and gets out of the way the moment they scroll up to read something older.
 */
export const ChatTranscript: React.FC<ChatTranscriptProps> = ({
  turns,
  onFilterChange,
  filteringTurnId,
  filterError,
}) => {
  const endRef = useRef<HTMLDivElement>(null);
  const pausedRef = useRef(false);
  const prevTurnCount = useRef(turns.length);

  // Track how far from the bottom the user is; > AUTO_SCROLL_PAUSE_PX pauses the chase.
  useEffect(() => {
    const onScroll = () => {
      const doc = document.documentElement;
      const distanceFromBottom = doc.scrollHeight - window.innerHeight - window.scrollY;
      pausedRef.current = distanceFromBottom > AUTO_SCROLL_PAUSE_PX;
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const last = turns[turns.length - 1];
  // Growth signals: new turn, more streamed prose, or another block hydrated.
  const growth = `${turns.length}:${last?.text.length ?? 0}:${last?.blocks.length ?? 0}`;

  useEffect(() => {
    const isNewTurn = turns.length !== prevTurnCount.current;
    prevTurnCount.current = turns.length;
    if (pausedRef.current) return;
    // A brand-new turn glides into view; mid-stream growth just keeps the bottom pinned.
    endRef.current?.scrollIntoView({ behavior: isNewTurn ? 'smooth' : 'auto', block: 'end' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [growth]);

  return (
    // pb-2 keeps the last message clear of the sticky composer that sits directly below it.
    <div className="flex flex-col gap-6 pb-2">
      {turns.map((turn) => (
        <ChatMessage
          key={turn.id}
          turn={turn}
          onFilterChange={onFilterChange}
          isFiltering={filteringTurnId === turn.id}
          filterError={filterError?.turnId === turn.id ? filterError.message : null}
        />
      ))}
      <div ref={endRef} />
    </div>
  );
};

export default ChatTranscript;
